/*
 * Email check for the account sign-up forms ({% form 'create_customer' %}: the /account/register
 * page, sections/main-customers-register.liquid, and the login drawer, sections/login-sidebar.liquid).
 *
 * Both forms are `novalidate`, so the browser never checks `type="email"` and, without this,
 * any text is posted and Shopify creates the account. This blocks the submit until the email
 * is well formed.
 *
 * It finds the forms by their hidden form_type field and builds its own error message, on
 * purpose: the theme caches the drawer's HTML in sessionStorage (`dataLoginT4s`, ~6.7 h, see
 * T4SThemeSP.Login in interactable.min.js), so the markup on screen can be older than the
 * deployed section. Messages come from the <script> tag's data-msg-* attributes (locale strings).
 *
 * The format is checked first, in the browser. Then a DNS lookup (domainAcceptsMail) decides whether
 * the domain receives mail at all; it is the source of truth, so a TLD created after this file was
 * written still works. The mistyped-endings rule (TYPO_TARGETS) only words the error ("did you mean
 * ana@gmail.com?") and, when DNS can't be reached, warns once instead of blocking. None of this can tell
 * whether the mailbox itself exists.
 */
(function () {
  if (window.SavvySignupEmail) return;

  var script = document.currentScript;
  var MESSAGES = {
    required: (script && script.getAttribute('data-msg-required')) || 'Ingresa tu correo electrónico.',
    invalid: (script && script.getAttribute('data-msg-invalid')) || 'Ingresa un correo electrónico válido, por ejemplo nombre@correo.com.',
    // {email} is replaced with the corrected address.
    typo: (script && script.getAttribute('data-msg-typo')) || 'Revisa el final de tu correo. ¿Quisiste decir {email}?',
    // Shown instead of `typo` when DNS couldn't confirm it: a second click goes ahead.
    typoUnverified: (script && script.getAttribute('data-msg-typo-unverified')) || '¿Quisiste decir {email}? Si tu correo está bien escrito, haz clic de nuevo para continuar.',
    // {domain} is replaced with the part after the @.
    domain: (script && script.getAttribute('data-msg-domain')) || 'El dominio {domain} no recibe correos. Revisa que tu correo esté bien escrito.'
  };
  var ICON = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-error" viewBox="0 0 13 13"><circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/><circle cx="6.5" cy="6.5" r="5.5" fill="var(--t4s-error-color)" stroke="var(--t4s-error-color)" stroke-width="0.7"/><path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"/></svg>';
  var counter = 0;

  // name@domain.tld: one @, no spaces, no empty or doubled dots, and a letters-only TLD of
  // 2+ characters. Stricter than the browser's type="email" check, which accepts "ana@gmail".
  var EMAIL_RE = /^[^\s@.]+(\.[^\s@.]+)*@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i;

  // Mistyped endings. An ending close to one of TYPO_TARGETS looks like a typo of it, unless it is
  // itself a real TLD. This rule never blocks on its own: it picks the message when DNS says the
  // domain doesn't exist, and when the lookup can't be done it only warns (see warnUnverifiedTypo). "Close" is counted in keystrokes (a letter added, missing, replaced,
  // or two swapped): up to 2 for .com, the one people mistype most (.conn, .cpmm), 1 for the rest
  // (.nett, .ogr, .ess). So .conn -> .com, but .co (Colombia), .cam or .gov stay valid.
  var TYPO_TARGETS = [['com', 2], ['net', 1], ['org', 1], ['es', 1]];
  // Every real TLD within that distance of a target, generated from
  // https://data.iana.org/TLD/tlds-alpha-by-domain.txt (version 2026101900). A TLD IANA adds later
  // and missing here costs, at worst, one extra click while DNS is unreachable (the warning).
  var REAL_NEAR_TARGETS = (
    'aco am ao aol as bet bm bo bom boo bot box bs ca cab cal cam camp car cat cba cbn cc cd ceo ' +
    'cf cfa cfd cg ch ci ck cl cm cn co cool coop cpa cr crs cu cv cw cx cy cyou cz dm do dog dot ' +
    'ec eco ee eg er esq et eu eus fm fo foo fox gm gmo gop got gov gs hm hot how ibm ifm im io ' +
    'is jm jo jot joy kim km lol ls mm mo moe moi mom mov ms ne nec new next no now ntt om ong ' +
    'ooo pet pm ps ro room rs scot se sm so soy ss tm to top uol us vet wow ws you zm'
  ).split(' ');
  // Typos further away than that, seen often enough to list.
  var EXTRA_TYPOS = { comcom: 'com' };

  // Keystrokes between a and b (optimal string alignment distance).
  function keystrokes(a, b) {
    var d = [], i, j;
    for (i = 0; i <= a.length; i++) d[i] = [i];
    for (j = 0; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      for (j = 1; j <= b.length; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  }

  // The ending the shopper meant (the closest target), or '' when this ending isn't a typo.
  function intendedTld(tld) {
    if (EXTRA_TYPOS[tld]) return EXTRA_TYPOS[tld];
    if (REAL_NEAR_TARGETS.indexOf(tld) !== -1) return '';
    var best = '', bestDistance = Infinity;
    for (var i = 0; i < TYPO_TARGETS.length; i++) {
      var distance = keystrokes(tld, TYPO_TARGETS[i][0]);
      if (distance > 0 && distance <= TYPO_TARGETS[i][1] && distance < bestDistance) {
        best = TYPO_TARGETS[i][0];
        bestDistance = distance;
      }
    }
    return best;
  }

  function isValid(value) {
    return EMAIL_RE.test(value) && !typoFix(value);
  }

  // The address with its mistyped ending corrected, or '' when the ending isn't a known typo.
  function typoFix(value) {
    var dot = value.lastIndexOf('.');
    var tld = value.slice(dot + 1);
    var fix = intendedTld(tld.toLowerCase());
    if (!fix) return '';
    // Keep the shopper's casing: ANA@HOTMAIL.CON -> ANA@HOTMAIL.COM, not ANA@HOTMAIL.com.
    return value.slice(0, dot + 1) + (tld === tld.toUpperCase() ? fix.toUpperCase() : fix);
  }

  function isSignupForm(form) {
    return !!(form && form.tagName === 'FORM' && form.querySelector('input[name="form_type"][value="create_customer"]'));
  }

  function emailInput(form) {
    return form.querySelector('input[name="customer[email]"]');
  }

  // customer.min.css only lays out .form__message (icon size, icon beside the text) inside
  // .t4s-customer, which wraps the /account/register form but not the drawer's, where the icon
  // would otherwise render at the browser's default SVG size. Same values, for this element only.
  function addStyles() {
    if (document.getElementById('sv-email-error-styles')) return;
    var style = document.createElement('style');
    style.id = 'sv-email-error-styles';
    style.textContent =
      '.sv-email-error{display:flex;align-items:center}' +
      '.sv-email-error svg{width:1.5rem;height:1.5rem;flex:none;margin-right:1rem}';
    document.head.appendChild(style);
  }

  // The error element sits right after the email field's wrapper (.t4s_field), where the
  // section puts Shopify's own server-side email error.
  function errorBox(input, create) {
    var anchor = input.closest('.t4s_field') || input;
    var box = anchor.nextElementSibling;
    if (box && box.hasAttribute('data-sv-email-error')) return box;
    if (!create) return null;
    addStyles();
    box = document.createElement('span');
    box.className = 'form__message sv-email-error';
    box.id = 'sv-email-error-' + (++counter);
    box.setAttribute('role', 'alert');
    box.setAttribute('data-sv-email-error', '');
    box.innerHTML = ICON + '<span data-sv-email-error-text></span>';
    anchor.parentNode.insertBefore(box, anchor.nextSibling);
    return box;
  }

  function showError(form, input, message) {
    var box = errorBox(input, true);
    box.querySelector('[data-sv-email-error-text]').textContent = message;
    box.style.display = '';
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', box.id);
    // The server's message from the previous attempt would contradict the new one.
    var serverError = form.querySelector('#RegisterForm-email-error');
    if (serverError) serverError.style.display = 'none';
  }

  function clearError(input) {
    var box = errorBox(input, false);
    if (!box || box.style.display === 'none') return;
    box.style.display = 'none';
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }

  // The format error for this form, or '' when the email is well formed.
  function formatError(form) {
    var input = emailInput(form);
    if (!input) return '';
    // Mobile keyboards and autofill often leave a trailing space.
    input.value = input.value.trim();
    if (input.value === '') return MESSAGES.required;
    if (!EMAIL_RE.test(input.value)) return MESSAGES.invalid;
    return '';
  }

  // The "did you mean" message when the ending looks mistyped, or ''.
  function typoError(form) {
    var fix = typoFix(emailInput(form).value);
    return fix ? MESSAGES.typo.replace('{email}', fix) : '';
  }

  // Without DNS, a mistyped-looking ending may be a real TLD this file doesn't know yet (added by
  // IANA later), so it only gets a warning, never a block: the first submit shows it, a second
  // submit of the same email goes through. Remembers, per form, the email already warned about.
  var warnedEmail = typeof WeakMap === 'function' ? new WeakMap() : null;

  // True when the submit must stop for the warning (it is shown), false when it may go ahead.
  function warnUnverifiedTypo(form) {
    var input = emailInput(form);
    var fix = typoFix(input.value);
    if (!fix || !warnedEmail || warnedEmail.get(form) === input.value) return false;
    warnedEmail.set(form, input.value);
    showError(form, input, MESSAGES.typoUnverified.replace('{email}', fix));
    input.focus();
    return true;
  }

  function block(event, form) {
    var message = formatError(form);
    var input = emailInput(form);
    if (!message) {
      if (input) clearError(input);
      return false;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    showError(form, input, message);
    input.focus();
    return true;
  }

  // Whether a domain can receive mail, asked of Google's public DNS-over-HTTPS resolver (only
  // the domain is sent, never the address). Resolves false only on a definite no: the domain
  // doesn't exist (NXDOMAIN), or it publishes a "null MX" (RFC 7505, "0 .") saying it takes no
  // mail. A domain with no MX record still gets mail through its A record (RFC 5321), so that
  // passes. Anything else (timeout, network error, blocked request, server failure) resolves
  // null, and the sign-up goes ahead: a real customer must never be stopped because the lookup
  // service is unreachable. Cloudflare's resolver was tried as a fallback and is blocked from
  // the storefront (checked 2026-10-19).
  var DNS_TIMEOUT_MS = 3000;
  // Answers so far, per domain (true = accepts mail, false = doesn't). Seeded with the common
  // providers so most sign-ups never make the request.
  var domainVerdicts = {
    'gmail.com': true, 'hotmail.com': true, 'outlook.com': true, 'yahoo.com': true, 'icloud.com': true,
    'live.com': true, 'msn.com': true, 'me.com': true, 'hotmail.es': true, 'outlook.es': true, 'yahoo.es': true
  };

  function domainAcceptsMail(domain) {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, DNS_TIMEOUT_MS);
    var url = 'https://dns.google/resolve?type=MX&name=' + encodeURIComponent(domain);
    return fetch(url, controller ? { signal: controller.signal } : {})
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (answer) {
        if (!answer) return null;
        if (answer.Status === 3) return false;
        if (answer.Status !== 0) return null;
        var mx = (answer.Answer || []).filter(function (record) { return record.type === 15; });
        if (mx.length === 1 && /^0 \.?$/.test(String(mx[0].data).trim())) return false;
        return true;
      })
      .catch(function () { return null; })
      .then(function (verdict) { clearTimeout(timer); return verdict; });
  }

  // The form being re-sent after a lookup that found nothing wrong (or couldn't answer): its next
  // submit skips the domain check once. The format and typo checks still run on it.
  var passOnce = null;

  function domainOf(form) {
    var input = emailInput(form);
    return input ? input.value.slice(input.value.lastIndexOf('@') + 1).toLowerCase() : '';
  }

  // The domain doesn't receive mail. When its ending also looks mistyped, the "did you mean"
  // message says more than "this domain doesn't receive mail", so it wins.
  function showDomainError(form, domain) {
    var input = emailInput(form);
    showError(form, input, typoError(form) || MESSAGES.domain.replace('{domain}', domain));
    input.focus();
  }

  // Capture phase on window: the earliest point a submit can be seen, ahead of any listener
  // on the document or the form itself, so no other script gets to send an invalid form.
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSignupForm(form) || block(event, form)) return;

    var domain = domainOf(form);
    if (passOnce === form) {
      passOnce = null;
      return;
    }
    if (!domain || domainVerdicts[domain] === true) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (domainVerdicts[domain] === false) {
      showDomainError(form, domain);
      return;
    }
    // Hold this submit while the domain is looked up, then send the form again: the second
    // submit finds the verdict cached and goes through untouched (Shopify's own listeners
    // included). Further clicks while waiting are swallowed.
    if (form.hasAttribute('data-sv-email-checking')) return;
    form.setAttribute('data-sv-email-checking', '');
    form.setAttribute('aria-busy', 'true');
    var submitter = event.submitter;
    domainAcceptsMail(domain).then(function (verdict) {
      form.removeAttribute('data-sv-email-checking');
      form.removeAttribute('aria-busy');
      // An unreachable lookup isn't cached, so the next attempt asks again.
      if (verdict !== null) domainVerdicts[domain] = verdict;
      // The shopper edited the email while we waited: their next click checks the new one.
      if (domainOf(form) !== domain) return;
      if (verdict === false) {
        showDomainError(form, domain);
        return;
      }
      // No answer from DNS: fall back to the offline endings rule, as a warning the shopper can
      // click past, so a TLD newer than REAL_NEAR_TARGETS is never locked out.
      if (verdict === null && warnUnverifiedTypo(form)) return;
      passOnce = form;
      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
        } else {
          nativeSubmit.call(form);
        }
      } finally {
        passOnce = null; // the re-sent submit fires synchronously, so it has already used it
      }
    });
  }, true);

  // form.submit() fires no submit event, so a script calling it would skip the listener above.
  // Nothing in the theme does that today; this keeps it that way for these forms. It can't wait
  // for the DNS lookup (submit() is synchronous), so it uses the offline checks: format, then the
  // endings rule, unless DNS has already answered for this domain.
  var nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    if (isSignupForm(this)) {
      var message = formatError(this);
      var verdict = domainVerdicts[domainOf(this)];
      if (!message && verdict === false) message = typoError(this) || MESSAGES.domain.replace('{domain}', domainOf(this));
      if (message) {
        showError(this, emailInput(this), message);
        return;
      }
      if (verdict !== true && warnUnverifiedTypo(this)) return;
    }
    return nativeSubmit.apply(this, arguments);
  };

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (input.name === 'customer[email]' && isSignupForm(input.form)) clearError(input);
  });

  window.SavvySignupEmail = { isValid: isValid };
})();
